/**
 * One Lykke conversation (the home one, or a campaign's), reusable anywhere: the thread (messages, decision cards,
 * "passed to Beatly" notes, navigation notes) and the composer (text, voice, files). It fills the box it is given: no
 * page-layout assumptions, so the Lykke page, the dock and a future app layout can mount it.
 *
 *   <LykkeConversation scope variant placeholder autoFocus emptyHint />
 *   scope    'home' (HOME) or a campaign id
 *   variant  'page' (roomy, full height) | 'dock' (compact)
 * Needs <LykkeProvider> above it (conversation state, navigation and voice live there).
 */
import { useEffect, useRef } from 'react';
import { FileText, Loader2, Mic, UserRound } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { useLykke, type Scope } from './LykkeContext';
import { DecisionCard } from './DecisionCard';
import { LykkeAvatar } from './LykkeAvatar';
import { LykkeComposer } from './LykkeComposer';
import type { AgentComposerHandle } from '@/cowork/shell/AgentComposer';

/** Light markdown: **bold** and line breaks (Lykke writes short lists). */
function Rich({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => {
        // "- item" lines read as a list (Lykke writes short lists), with a bullet and a hanging indent.
        const bullet = /^\s*[-•*]\s+/.test(line);
        const body = bullet ? line.replace(/^\s*[-•*]\s+/, '') : line;
        return (
          <span key={i} className={bullet ? 'relative block min-h-[0.5em] pl-4' : 'block min-h-[0.5em]'}>
            {bullet && <span className="absolute left-1 top-0" aria-hidden>•</span>}
            {body.split(/(\*\*[^*]+\*\*)/g).map((part, j) => (part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong> : <span key={j}>{part}</span>))}
          </span>
        );
      })}
    </>
  );
}

export interface LykkeConversationProps {
  scope: Scope;
  variant: 'page' | 'dock';
  placeholder: string;
  autoFocus?: boolean;
  emptyHint?: string;
  /** The composer, for a parent that puts the cursor in it (the new layout's "Ask Lykke"). */
  composerRef?: React.Ref<AgentComposerHandle>;
}

export function LykkeConversation({ scope, variant, placeholder, autoFocus, emptyHint, composerRef }: LykkeConversationProps) {
  const l = useLykke();
  const end = useRef<HTMLDivElement>(null);
  const items = l?.items(scope) || [];
  const busy = l?.busy(scope) || false;
  const working = l?.working(scope) || null;
  const loaded = l?.loaded(scope) || false;

  const asked = useRef<string | null>(null);
  const available = !!l?.available;
  const load = l?.load;
  useEffect(() => { if (available && load && !loaded && asked.current !== scope) { asked.current = scope; void load(scope); } }, [available, load, scope, loaded]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [items.length, busy, working]);
  if (!l?.available) return null;
  const page = variant === 'page';

  return (
    <div className="flex h-full min-h-0 flex-col" data-lykke-conversation={scope}>
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', page ? 'gap-3 px-1 py-2' : 'gap-2.5 px-4 py-3')} data-lykke-thread>
        {!items.length && emptyHint && <p className="py-2 text-[13px] text-[var(--shell-muted,#667085)]">{loaded ? emptyHint : ''}</p>}
        {items.map((m) => m.role === 'user' ? (
          <div key={m.id} className={cn('max-w-[85%] self-end rounded-2xl rounded-br-md bg-[var(--shell-bubble,#f2f4f7)] px-3 py-1.5 text-[var(--shell-text,#101828)]', page ? 'text-[15px]' : 'text-[14px]')}>
            {m.via === 'voice' && <Mic className="mr-1 inline h-3 w-3 -translate-y-px text-[#98a2b3]" />}{m.text}
            {m.files?.length ? <span className="mt-1 flex flex-wrap gap-1">{m.files.map((f) => <span key={f} className="inline-flex items-center gap-1 rounded bg-white/70 px-1.5 py-0.5 text-[11.5px] text-[#475467]"><FileText className="h-3 w-3" />{f}</span>)}</span> : null}
          </div>
        ) : m.note ? (
          <div key={m.id} className="self-start rounded-lg bg-[var(--shell-hover,#f9fafb)] px-2.5 py-1.5 text-[12.5px] text-[var(--shell-muted,#475467)]" data-lykke-note>{m.text}</div>
        ) : m.role === 'nav' ? (
          <div key={m.id} className="self-start rounded-full bg-[#a9f600]/30 px-2 py-0.5 text-[12px] font-semibold text-[#3b5a00]" data-lykke-nav>{m.text}</div>
        ) : m.role === 'error' ? (
          <div key={m.id} className="self-start text-[13px] text-[#b42318]">{m.text}</div>
        ) : (
          <div key={m.id} className="flex max-w-[92%] items-start gap-2 self-start" data-lykke-reply>
            {m.fromBeatly ? <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#2e024c] text-white"><UserRound className="h-3.5 w-3.5" /></span> : <LykkeAvatar size={page ? 28 : 24} />}
            <div className="min-w-0 flex-1">
              {m.pending && !m.text ? (
                <div className="flex items-center gap-2 py-1 text-[13px] text-[#667085]"><Loader2 className="h-3.5 w-3.5 animate-spin" />{working || 'Thinking'}</div>
              ) : (
                <div className={cn('leading-[1.5] text-[var(--shell-text,#101828)]', page ? 'text-[15px]' : 'text-[14px]')}><Rich text={m.text} /></div>
              )}
              {m.decision && <DecisionCard card={m.decision} />}
              {m.request && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-[#2e024c]/5 px-2 py-1 text-[12px] text-[#2e024c]" data-lykke-request>
                  Passed to {m.request.who}: answer {m.request.by}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <div className={page ? 'pt-3' : 'mx-3 mb-3'}>
        <LykkeComposer ref={composerRef} scope={scope} size={page ? 'large' : 'compact'} placeholder={placeholder} autoFocus={autoFocus} busy={busy} onSend={(text, files, raw) => void l.send(scope, text, undefined, files, raw)} />
      </div>
    </div>
  );
}
